import * as types from '../actiontypes';
import {GetFormValidationsUrl, GetAllValidationsUrl, GetAllValidationsUrlPOST} from '../apiUrls';
import {
	ValidationGroup,
	documentTypes,
	validationFilter,
	formBodyType,
	summaryFilters,
	navigationFilters
} from '../../util/uiconstants';
import Utility from '../../util/utilityfunctions';
import {loadStorage} from '../../components/Common/localStorage';

const commonValidationParams = {
	includeCount: true,
	includeDetail: true
};

//Executes Validations Api (not Forms validations).
//EngagementId: number
//Documents: [{ id: string, documentTypeId: number}];
export const getDocumentValidations = (
	engagementId,
	documents,
	embeddedFormBodyTypeIds = undefined,
	bodyTypeId,
	validationGroup,
	filter = undefined,
	isForDocumentsWithoutApprovals = false
) => {
	if (documents && documents.length > 0) {
		const documentIds = documents?.map((d) => d?.id);
		const documentTypeIds = documents?.map((d) => d?.documentTypeId);
		const validationGroups = getValidationGroups(documentTypeIds, bodyTypeId);

		if (
			validationGroups.length > 0 &&
			((bodyTypeId === formBodyType.ALRAAccountList &&
				validationGroups[0] === ValidationGroup.AccountsWithoutIndividualALRADocument) ||
				(bodyTypeId === formBodyType.DocumentUploadLegend &&
					validationGroups[0] === ValidationGroup.DocumentsMissingRequiredPICEQRSignOff) ||
				(filter &&
					(filter === navigationFilters.INCOMPLETERESPONSES || filter === navigationFilters.UNRESOLVEDCOMMENTS)))
		) {
			commonValidationParams.hasIncompleteResponse = true;
		} else {
			delete commonValidationParams.hasIncompleteResponse;
		}

		if (validationGroup && validationGroups.indexOf(validationGroup) === -1) {
			validationGroups.push(validationGroup);
		}

		const queryStringParameters = {
			...commonValidationParams,
			validationGroups,
			filteredDocumentIds: documentIds.length === 1 ? documentIds[0] : documentIds.join(','),
			embeddedFormBodyTypeIds
		};

		if (documentIds.length > 1) {
			queryStringParameters.includeChildren = false;
			queryStringParameters.includeDetail = false;
		}

		if (embeddedFormBodyTypeIds && embeddedFormBodyTypeIds.length) {
			queryStringParameters.includeChildren = false;
			queryStringParameters.includeDetail = false;
		}

		const url = GetAllValidationsUrl(engagementId, queryStringParameters);

		return {
			type: isForDocumentsWithoutApprovals ? types.GET_WITHOUT_APPROVAL_VALIDATION_COUNTS : types.GET_VALIDATIONCOUNTS,
			documentIds,
			meta: {
				api: {
					url,
					method: 'GET'
				}
			}
		};
	}
};

//Executes Validations Api POST(not Forms validations).
//EngagementId: number
//Documents: list of document;
export const getDocumentValidationsPOST = (
	engagementId,
	documents,
	embeddedFormBodyTypeIds = undefined,
	bodyTypeId,
	validationGroup,
	isForDocumentsWithoutApprovals = false,
	hasIncompleteResponse,
	documentId = undefined // This is used for external user
) => {
	if (documents && documents.length > 0) {
		const documentIds = documents?.map((d) => d?.id);
		const documentTypeIds = documents?.map((d) => d?.documentTypeId);
		const validationGroups = getValidationGroups(documentTypeIds, bodyTypeId);

		if (validationGroup && validationGroups.indexOf(validationGroup) === -1) {
			validationGroups.push(validationGroup);
		}

		const payload = {
			...commonValidationParams,
			validationGroups,
			embeddedFormBodyTypeIds,
			includeCount: true,
			hasIncompleteResponse,
			filteredDocumentIds: documentIds,
		};

		if (documentIds.length > 1) {
			payload.includeDetail = false;
		}

		if (embeddedFormBodyTypeIds && embeddedFormBodyTypeIds.length) {
			payload.includeDetail = false;
		}

		const queryStringParameters = {
			documentId: documentId, // This is used for external user, to identify which document validations are being fetched as documentId will have multiple documents
		};

		const url = GetAllValidationsUrlPOST(engagementId, queryStringParameters);

		return {
			type: isForDocumentsWithoutApprovals ? types.GET_WITHOUT_APPROVAL_VALIDATION_COUNTS : types.GET_VALIDATIONCOUNTS,
			documentIds,
			meta: {
				api: {
					url,
					method: 'POST',
					data: payload
				}
			}
		};
	}
};

//Executes Validations Api (not Forms validations).
//EngagementId: number
//Documents: [{ id: string, documentTypeId: number}];
export const getEmbeddedDocumentValidations = (
	engagementId,
	documents,
	embeddedFormBodyTypeIds = undefined,
	validationGroup,
	embeddedDocumentIds = undefined,
	documentGroupId
) => {
	if (documents && documents.length > 0 && embeddedDocumentIds && embeddedDocumentIds.length > 0) {
		const documentIds = documents.map((d) => d.id);
		const validationGroups = [];

		if (validationGroup) {
			validationGroups.push(validationGroup);
		}

		const url = GetAllValidationsUrl(engagementId, {
			...commonValidationParams,
			validationGroups,
			includeChildren: false,
			documentGroups: documentGroupId
		});

		return {
			type: types.GET_VALIDATIONCOUNTS,
			documentIds,
			meta: {
				api: {
					url,
					method: 'GET'
				}
			},
			embeddedDocumentIds
		};
	}
};

//Executes Validations Api (not Forms validations).
//EngagementId: number
//Documents: [{ id: string, documentTypeId: number}];
export const getEmbeddedDocumentValidationsPOST = (
	engagementId,
	documents,
	validationGroup,
	embeddedDocumentIds = undefined,
	documentGroupId,
	hasIncompleteResponse
) => {
	if (documents && documents.length > 0 && embeddedDocumentIds && embeddedDocumentIds.length > 0) {
		const documentIds = documents.map((d) => d.id);
		const validationGroups = [];

		if (validationGroup) {
			validationGroups.push(validationGroup);
		}

		const url = GetAllValidationsUrl(engagementId, {
			...commonValidationParams,
			validationGroups,
			includeChildren: false,
			documentGroups: documentGroupId
		});

		const payload = {
			hasIncompleteResponse,
			includeCount: true,
			includeDetail: true,
			validationGroups: [ValidationGroup.GuidedWorkFlow],
			filteredDocumentIds: embeddedDocumentIds
		};

		return {
			type: types.GET_VALIDATIONCOUNTS,
			documentIds,
			meta: {
				api: {
					url,
					method: 'POST',

					data: payload
				}
			},
			embeddedDocumentIds
		};
	}
};

export const getUnresolvedCommentsCount = (
	engagementId,
	documentId,
	documentTypeId,
	payload,
	embeddedFormBodyTypeIds,
	filteredDocumentIds
) => {
	if (documentId && documentId !== '') {
		const ValidationGroups = getValidationGroup(documentTypeId);
		const sessionStorageKey = `ares_engagement_${engagementId}_document_${documentId}_validationsFilter`;
		const filter = loadStorage(sessionStorageKey);
		if (
			documentTypeId === documentTypes.PROFILEV2 ||
			documentTypeId === documentTypes.INDEPENDENCEFORMINDIVIDUAL ||
			documentTypeId === documentTypes.MULTI_ENTITY_INDIVIDUAL_PROFILE_V2_NO_SIGN_OFF_REQUIRED ||
			filter === validationFilter.INCOMPLETE ||
			filter === validationFilter.COMMENTS ||
			filter === summaryFilters.INCOMPLETERESPONSES ||
			filter === summaryFilters.UNRESOLVEDCOMMENTS
		) {
			commonValidationParams.hasIncompleteResponse = true;
		} else {
			delete commonValidationParams.hasIncompleteResponse;
		}

		let queryStringParameters = {
			...commonValidationParams,
			ValidationGroups,
			embeddedFormBodyTypeIds
		};

		let url = GetFormValidationsUrl(engagementId, documentId, queryStringParameters);

		if (documentTypeId === documentTypes.INDEPENDENCEFORMINDIVIDUAL) {
			queryStringParameters = {
				...commonValidationParams,
				ValidationGroups,
				filteredDocumentIds: filteredDocumentIds?.join(','),
				embeddedFormBodyTypeIds
			};

			if (filteredDocumentIds && filteredDocumentIds.length > 1) {
				queryStringParameters.includeChildren = false;
				queryStringParameters.includeDetail = false;
			}

			url = GetFormValidationsUrl(engagementId, documentId, queryStringParameters);
		}

		return {
			type: types.GET_VALIDATIONCOUNTS,
			documentIds: filteredDocumentIds !== undefined ? [documentId].concat(filteredDocumentIds) : [documentId],
			documentTypeId: documentTypeId,
			...payload,
			meta: {
				api: {
					url,
					method: 'GET'
				}
			}
		};
	}
};

function getValidationGroup(documentTypeId, bodyTypeId) {
	if (documentTypeId === documentTypes.INDEPENDENCEFORMINDIVIDUAL) {
		return ValidationGroup.IndependenceV2Validations;
	} else if (documentTypeId === documentTypes.PROFILEV2) {
		return ValidationGroup.ProfileV2Validations;
	} else if (documentTypeId === documentTypes.IT_FLOW_ISA315) {
		return ValidationGroup.ITFlowCanvasForm;
	} else if (Utility.isDocumentTypeFit(documentTypeId)) {
		if (bodyTypeId === formBodyType.ALRAAccountList) {
			return ValidationGroup.AccountsWithoutIndividualALRADocument;
		} else if (bodyTypeId === formBodyType.DocumentUploadLegend) {
			return ValidationGroup.DocumentsMissingRequiredPICEQRSignOff;
		} else if (bodyTypeId === formBodyType.EstimateListing) {
			return ValidationGroup.EstimatesWithoutIndividualDocument;
		}
		return ValidationGroup.GuidedWorkFlow;
	} else {
		return ValidationGroup.CanvasForm;
	}
}

function getValidationGroups(documentTypeIds, bodyTypeId) {
	let result = [];
	let group;

	for (let i = 0; i < documentTypeIds.length; i++) {
		group = getValidationGroup(documentTypeIds[i], bodyTypeId);

		if (result.indexOf(group) === -1) {
			result.push(group);
		}
	}

	return result;
}

export function resetValidationForDocumentId(documentId) {
	return {
		type: types.UNLOAD_VALIDATION_COUNTS,
		documentId
	};
}

export const getAuditPlanSummaryValidations = (engagementId, documentId, validationGroups) => {
	if (documentId && documentId !== '') {
		const queryStringParameters = {
			includeCount: true,
			includeDetail: true,
			ValidationGroups: validationGroups,
			filteredDocumentIds: documentId
		};

		const url = GetAllValidationsUrl(engagementId, queryStringParameters);

		return {
			type: types.GET_AUDIT_PLAN_SUMMARY_VALIDATIONS,
			meta: {
				api: {
					url,
					method: 'GET'
				}
			}
		};
	}
};

export const clearAuditPlanSummaryValidations = () => ({
	type: types.CLEAR_AUDIT_PLAN_SUMMARY_VALIDATIONS
});
